import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { db, getCustomerOrders } from '../services/firebase';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useBrand } from '../context/BrandContext';

interface TrackedOrder {
  id: string;
  order_number: string;
  status: string;
  total: number;
  items_count: number;
  order_type: string;
  created_at: Date;
  customer_name: string;
  delivery_address?: string;
  table_number?: number;
  rider_name?: string;
  rider_email?: string;
  discount_amount?: number;
  total_before_discount?: number;
}

const STATUS_CONFIG: Record<string, { color: string; labelDe: string; labelEn: string }> = {
  pending:     { color: '#FFA726', labelDe: 'Ausstehend',        labelEn: 'Pending' },
  confirmed:   { color: '#42A5F5', labelDe: 'Bestatigt',         labelEn: 'Confirmed' },
  preparing:   { color: '#AB47BC', labelDe: 'Wird zubereitet',   labelEn: 'Preparing' },
  ready:       { color: '#66BB6A', labelDe: 'Bereit',            labelEn: 'Ready' },
  assigned:    { color: '#29B6F6', labelDe: 'Fahrer zugewiesen',  labelEn: 'Driver Assigned' },
  picked_up:   { color: '#26C6DA', labelDe: 'Vom Fahrer abgeholt', labelEn: 'Picked Up by Driver' },
  in_transit:  { color: '#29B6F6', labelDe: 'Unterwegs',         labelEn: 'On the Way' },
  on_delivery: { color: '#29B6F6', labelDe: 'Unterwegs',         labelEn: 'On the Way' },
  on_the_way:  { color: '#29B6F6', labelDe: 'Unterwegs',         labelEn: 'On the Way' },
  completed:   { color: '#94e815', labelDe: 'Abgeschlossen',     labelEn: 'Completed' },
  delivered:   { color: '#94e815', labelDe: 'Geliefert',         labelEn: 'Delivered' },
  cancelled:   { color: '#EF5350', labelDe: 'Storniert',         labelEn: 'Cancelled' },
};

const OrderTrackingPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { lang, setLang } = useLanguage();
  const { brand } = useBrand();
  const isDE = lang === 'de';

  const [orders, setOrders] = useState<TrackedOrder[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }

    const orderMap = new Map<string, TrackedOrder>();
    const unsubscribers: (() => void)[] = [];
    // Failsafe: if nothing resolves in 15s (network stall, permission block),
    // drop the spinner so the page renders the empty state instead of hanging.
    const loadingFailsafe = setTimeout(() => {
      console.warn('[ORDERS] Loading timeout reached — forcing render of empty state');
      setLoading(false);
    }, 15000);

    const parseOrder = (d: any): TrackedOrder => {
      const data = d.data();
      return {
        id: d.id,
        order_number: String(data.order_number || d.id),
        status: data.status || 'pending',
        total: data.total || 0,
        items_count: (data.items || []).length,
        order_type: data.order_type || 'delivery',
        created_at: data.created_at?.toDate?.() || new Date(),
        customer_name: data.customer_name || '',
        delivery_address: data.delivery_address || '',
        table_number: data.table_number,
        rider_name: data.rider_name || '',
        rider_email: data.rider_email || '',
        discount_amount: parseFloat(data.discount_amount) || 0,
        total_before_discount: parseFloat(data.total_before_discount) || 0,
      };
    };

    const updateOrders = () => {
      const list = Array.from(orderMap.values());
      list.sort((a, b) => b.created_at.getTime() - a.created_at.getTime());
      setOrders(list);
      setLoading(false);
      clearTimeout(loadingFailsafe);
    };

    // Try OTP session-based fetch first (works for OTP users without Firebase auth)
    const sessionToken = (() => {
      try { return JSON.parse(localStorage.getItem('sushiki_session') || '{}').sessionToken || ''; } catch { return ''; }
    })();

    if (sessionToken) {
      // OTP user: fetch via cloud function (avoids list permission issues)
      getCustomerOrders({ sessionToken })
        .then((result: any) => {
          const data = result.data;
          if (data?.orders) {
            data.orders.forEach((o: any) => {
              orderMap.set(o.id, {
                id: o.id,
                order_number: String(o.order_number || o.id),
                status: o.status || 'pending',
                total: o.total || 0,
                items_count: o.items_count || 0,
                order_type: o.order_type || 'delivery',
                created_at: o.created_at ? new Date(o.created_at) : new Date(),
                customer_name: '',
              });
            });
            updateOrders();

            // Subscribe to real-time updates for each order (single doc reads, no list permission)
            data.orders.forEach((o: any) => {
              const { doc } = require('firebase/firestore');
              const unsub = onSnapshot(doc(db, 'orders', o.id), (snap: any) => {
                if (snap.exists()) {
                  orderMap.set(snap.id, parseOrder(snap));
                  updateOrders();
                }
              });
              unsubscribers.push(unsub);
            });
          } else {
            updateOrders();
          }
        })
        .catch((err) => {
          console.error('[ORDERS] Failed to fetch via cloud function:', err);
          setLoading(false);
          clearTimeout(loadingFailsafe);
        });
    } else if (user.email) {
      // Firebase Auth user with email — use direct Firestore queries
      const ordersRef = collection(db, 'orders');
      const unsub1 = onSnapshot(
        query(ordersRef, where('customer_email', '==', user.email)),
        (snap) => {
          snap.forEach((d) => orderMap.set(d.id, parseOrder(d)));
          updateOrders();
        },
        () => updateOrders()
      );
      const unsub2 = onSnapshot(
        query(ordersRef, where('customer_uid', '==', user.uid)),
        (snap) => {
          snap.forEach((d) => orderMap.set(d.id, parseOrder(d)));
          updateOrders();
        },
        () => updateOrders()
      );
      unsubscribers.push(unsub1, unsub2);
    } else {
      setLoading(false);
      clearTimeout(loadingFailsafe);
    }

    return () => {
      unsubscribers.forEach(u => u());
      clearTimeout(loadingFailsafe);
    };
  }, [user]);

  const formatPrice = (price: number) =>
    new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(price);

  const formatTime = (d: Date) =>
    d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });

  const formatDate = (d: Date) =>
    d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });

  const isToday = (d: Date) => {
    const today = new Date();
    return d.getDate() === today.getDate() && d.getMonth() === today.getMonth() && d.getFullYear() === today.getFullYear();
  };

  const activeStatuses = ['pending', 'confirmed', 'preparing', 'ready', 'assigned', 'picked_up', 'in_transit', 'on_delivery', 'on_the_way'];
  const activeOrders = orders.filter(o => activeStatuses.includes(o.status));
  const pastOrders = orders.filter(o => !activeStatuses.includes(o.status)).slice(0, 20);

  if (!user) {
    return (
      <div className="page tracking-page">
        <header className="menu-header">
          <button className="back-btn" onClick={() => navigate('/')}>&#8592;</button>
          <div className="header-info">
            <span className="header-brand">{isDE ? 'Meine Bestellungen' : 'My Orders'}</span>
          </div>
        </header>
        <div style={{ textAlign: 'center', padding: '60px 24px', color: '#888' }}>
          <p style={{ fontSize: '1rem', marginBottom: 16 }}>
            {isDE ? 'Bitte melden Sie sich an, um Ihre Bestellungen zu sehen.' : 'Please sign in to view your orders.'}
          </p>
          <button className="btn-primary" onClick={() => navigate('/auth')}>
            {isDE ? 'Anmelden' : 'Sign In'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="page tracking-page">
      <header className="menu-header">
        <button className="back-btn" onClick={() => navigate('/')}>&#8592;</button>
        <div className="header-info">
          <span className="header-brand">{isDE ? 'Meine Bestellungen' : 'My Orders'}</span>
        </div>
        <div className="lang-toggle" style={{ position: 'relative', top: 'auto', right: 'auto', marginLeft: 'auto' }}>
          <button className={lang === 'de' ? 'active' : ''} onClick={() => setLang('de')}>DE</button>
          <button className={lang === 'en' ? 'active' : ''} onClick={() => setLang('en')}>EN</button>
        </div>
      </header>

      <div style={{ padding: '0 16px 100px', maxWidth: 600, margin: '0 auto', width: '100%' }}>
        {loading ? (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '60vh',
            padding: 40,
            gap: 16,
            color: '#888',
          }}>
            <div className="spinner" />
            <p style={{ margin: 0, fontSize: '0.9rem' }}>
              {isDE ? 'Bestellungen werden geladen…' : 'Loading your orders…'}
            </p>
          </div>
        ) : orders.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px 0', color: '#888' }}>
            <p>{isDE ? 'Noch keine Bestellungen.' : 'No orders yet.'}</p>
            <button className="btn-primary" style={{ marginTop: 16 }} onClick={() => navigate('/menu/sushiki')}>
              {isDE ? 'Jetzt bestellen' : 'Order Now'}
            </button>
          </div>
        ) : (
          <>
            {/* Active Orders */}
            {activeOrders.length > 0 && (
              <>
                <h2 className="tracking-section-title">
                  <span className="tracking-dot active" />
                  {isDE ? 'Aktive Bestellungen' : 'Active Orders'}
                </h2>
                {activeOrders.map((order) => {
                  const cfg = STATUS_CONFIG[order.status] || STATUS_CONFIG.pending;
                  return (
                    <button
                      key={order.id}
                      className="tracking-card active"
                      onClick={() => navigate(`/confirmed/${order.order_number}`, { state: { orderId: order.id } })}
                    >
                      <div className="tc-top">
                        <span className="tc-number">#{order.order_number}</span>
                        <span className="tc-badge" style={{ background: cfg.color }}>
                          {isDE ? cfg.labelDe : cfg.labelEn}
                        </span>
                      </div>
                      <div className="tc-mid">
                        <span className="tc-type">
                          {order.order_type === 'delivery' ? (isDE ? 'Lieferung' : 'Delivery') :
                           order.order_type === 'pickup' ? (isDE ? 'Abholung' : 'Pickup') :
                           `${isDE ? 'Tisch' : 'Table'} ${order.table_number || ''}`}
                        </span>
                        <span className="tc-items">{order.items_count} {isDE ? 'Artikel' : 'items'}</span>
                      </div>
                      {/* Rider info */}
                      {order.rider_name && (
                        <div className="tc-rider">
                          <span>{isDE ? 'Fahrer' : 'Driver'}: {order.rider_name}</span>
                        </div>
                      )}
                      <div className="tc-bottom">
                        <span className="tc-time">{isToday(order.created_at) ? formatTime(order.created_at) : formatDate(order.created_at)}</span>
                        <span className="tc-total" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2 }}>
                          {order.discount_amount && order.discount_amount > 0 && order.total_before_discount ? (
                            <span style={{ fontSize: '0.7rem', color: '#9ca3af', textDecoration: 'line-through', lineHeight: 1 }}>
                              {formatPrice(order.total_before_discount)}
                            </span>
                          ) : null}
                          <span>{formatPrice(order.total)}</span>
                          {order.discount_amount && order.discount_amount > 0 ? (
                            <span style={{ fontSize: '0.62rem', color: '#16a34a', fontWeight: 700, lineHeight: 1 }}>
                              −{formatPrice(order.discount_amount)} {isDE ? 'Rabatt' : 'Discount'}
                            </span>
                          ) : null}
                        </span>
                      </div>
                      <div className="tc-arrow">&#8250;</div>
                    </button>
                  );
                })}
              </>
            )}

            {/* Past Orders */}
            {pastOrders.length > 0 && (
              <>
                <h2 className="tracking-section-title" style={{ marginTop: activeOrders.length > 0 ? 24 : 0 }}>
                  {isDE ? 'Vergangene Bestellungen' : 'Past Orders'}
                </h2>
                {pastOrders.map((order) => {
                  const cfg = STATUS_CONFIG[order.status] || STATUS_CONFIG.completed;
                  return (
                    <button
                      key={order.id}
                      className="tracking-card"
                      onClick={() => navigate(`/confirmed/${order.order_number}`, { state: { orderId: order.id } })}
                    >
                      <div className="tc-top">
                        <span className="tc-number">#{order.order_number}</span>
                        <span className="tc-badge" style={{ background: cfg.color, opacity: 0.7 }}>
                          {isDE ? cfg.labelDe : cfg.labelEn}
                        </span>
                      </div>
                      <div className="tc-bottom">
                        <span className="tc-time">{formatDate(order.created_at)} {formatTime(order.created_at)}</span>
                        <span className="tc-total">{formatPrice(order.total)}</span>
                      </div>
                      <div className="tc-arrow">&#8250;</div>
                    </button>
                  );
                })}
              </>
            )}
          </>
        )}
      </div>

      {/* Floating WhatsApp chat button — branded icon, opens WhatsApp */}
      <a
        href="https://wa.me/4922337063324"
        target="_blank"
        rel="noopener noreferrer"
        aria-label="WhatsApp"
        style={{
          position: 'fixed',
          bottom: 24, right: 24,
          zIndex: 1000,
          width: 60, height: 60,
          borderRadius: '50%',
          background: brand.bgElev,
          border: `2px solid ${brand.primary}`,
          display: 'grid', placeItems: 'center',
          boxShadow: `0 12px 30px ${brand.primary}55`,
          textDecoration: 'none',
        }}
      >
        <img
          src={brand.id === 'donvito' ? '/donvito_mark_light.png' : '/sushiki_mark_white.svg'}
          alt=""
          style={{ width: 36, height: 36, objectFit: 'contain' }}
        />
      </a>
    </div>
  );
};

export default OrderTrackingPage;
